/**
 * Which layout the generic record page draws: `today` (the header as One draws it now) or `tabs` (the proposed short
 * header and tabs, docs/features/one-record-layout/01_mock_up.md). A mock-only switch so Ben can compare the two: the
 * mock app with drafts defaults to `tabs` and offers the switch in the account menu; everywhere else the default is
 * `today` and nothing changes unless a link says `?layout=tabs`. A link's choice is remembered on the device. A
 * preference, never a gate: both layouts show the same commands and facts.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { usePlatformCapabilities } from "../../capabilities/context";

export type RecordLayoutMode = "today" | "tabs";

export const RECORD_LAYOUT_STORAGE_KEY = "cis.recordLayout";
/** The link's parameter: `?layout=today` or `?layout=tabs`. */
export const RECORD_LAYOUT_PARAM = "layout";

/** A stored or linked value as a layout; anything else (nothing, an old value) is none. */
export function parseRecordLayoutMode(
  value: string | null | undefined,
): RecordLayoutMode | null {
  return value === "today" || value === "tabs" ? value : null;
}

export interface RecordLayoutDefaults {
  /** The layout when neither the link nor the device has chosen one. */
  fallback: RecordLayoutMode;
  /** Whether the account menu offers the switch (mock mode with drafts). */
  isSwitchOffered: boolean;
}

const PRODUCTION: RecordLayoutDefaults = {
  fallback: "today",
  isSwitchOffered: false,
};

const RecordLayoutDefaultsContext =
  createContext<RecordLayoutDefaults>(PRODUCTION);

export function RecordLayoutDefaultsProvider({
  defaults,
  children,
}: {
  defaults: RecordLayoutDefaults;
  children: ReactNode;
}) {
  return createElement(
    RecordLayoutDefaultsContext.Provider,
    { value: defaults },
    children,
  );
}

/** The layout in force: the link's, else the device's, else the default. */
export function resolveRecordLayoutMode(
  linked: RecordLayoutMode | null,
  stored: RecordLayoutMode | null,
  fallback: RecordLayoutMode,
): RecordLayoutMode {
  return linked ?? stored ?? fallback;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useRecordLayoutMode(): {
  /** What this device chose, if anything. */
  stored: RecordLayoutMode | null;
  fallback: RecordLayoutMode;
  isSwitchOffered: boolean;
  /** Remember a layout on this device (null forgets it, back to the default). */
  setStored: (next: RecordLayoutMode | null) => void;
} {
  const capabilities = usePlatformCapabilities();
  const defaults = useContext(RecordLayoutDefaultsContext);
  const stored = parseRecordLayoutMode(
    useSyncExternalStore(subscribe, () =>
      capabilities.getStorageItem(RECORD_LAYOUT_STORAGE_KEY),
    ),
  );
  const setStored = useCallback(
    (next: RecordLayoutMode | null) => {
      capabilities.setStorageItem(RECORD_LAYOUT_STORAGE_KEY, next);
      listeners.forEach((l) => l());
    },
    [capabilities],
  );
  return {
    stored,
    fallback: defaults.fallback,
    isSwitchOffered: defaults.isSwitchOffered,
    setStored,
  };
}
